import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, MessageCircle, Flame, Trophy, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const categoryBadge = {
  standup: { label: "STAND-UP", cls: "bg-amber-400/10 text-amber-400 border-amber-400/40" },
  skit: { label: "SKIT", cls: "bg-crimson-500/10 text-[#FF2E55] border-[#FF2E55]/40" },
};

export function YoutubeEmbed({ youtubeId, title, autoplay = false }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-white/10 bg-black">
      <iframe
        title={title}
        src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1${autoplay ? "&autoplay=1" : ""}`}
        allow="accelerated-encoders; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}

export default function ClipCard({ clip, rank, onUpvoteChange }) {
  const { user } = useAuth();
  const [upvotes, setUpvotes] = useState(clip.upvotes);
  const [busy, setBusy] = useState(false);
  const cat = categoryBadge[clip.category] || categoryBadge.standup;

  const handleUpvote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to vote");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/clips/${clip.id}/upvote`);
      setUpvotes(data.upvotes);
      if (onUpvoteChange) onUpvoteChange(clip.id, data.upvotes);
      toast.success(data.upvoted ? "Upvoted 🎤" : "Vote removed");
    } catch (err) {
      toast.error("Failed to vote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="card-neon group relative overflow-hidden rounded-xl p-4"
      data-testid={`clip-card-${clip.id}`}
    >
      {rank !== undefined && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className={`flex h-14 w-14 items-center justify-center rounded-full font-display text-3xl ${
            rank === 1 ? "bg-amber-400 text-black glow-amber" :
            rank === 2 ? "bg-slate-300 text-black" :
            rank === 3 ? "bg-orange-400 text-black" :
            "bg-white/10 text-white"
          }`}>
            {rank}
          </div>
        </div>
      )}

      <Link to={`/clip/${clip.id}`} className="block">
        <div className="relative">
          <YoutubeEmbed youtubeId={clip.youtube_id} title={clip.title} />
          <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
            <span className="flex items-center gap-1 text-amber-400 text-xs font-mono-ui uppercase tracking-widest">
              <Play className="h-3 w-3" /> Watch full
            </span>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest ${cat.cls}`}>
              {cat.label}
            </span>
            <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-slate-400">
              {clip.era}
            </span>
            <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-slate-400">
              {clip.style?.replace("_", " ")}
            </span>
          </div>
          <h3 className="font-display text-xl leading-tight text-white group-hover:text-amber-400 transition-colors line-clamp-2 tracking-wide">
            {clip.title}
          </h3>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span className="text-[#00F0FF]">◆</span>
            <span>{clip.comedian}</span>
          </div>
        </div>
      </Link>

      <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
        <button
          onClick={handleUpvote}
          disabled={busy}
          data-testid={`upvote-btn-${clip.id}`}
          className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-sm font-bold text-white transition hover:border-amber-400/60 hover:bg-amber-400/10 hover:text-amber-400 disabled:opacity-50 btn-brutal"
        >
          <ArrowUp className="h-4 w-4" />
          <span data-testid={`upvote-count-${clip.id}`}>{upvotes}</span>
        </button>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-1 font-mono-ui" title="ELO Rating">
            <Trophy className="h-3.5 w-3.5 text-amber-400" />
            <span data-testid={`elo-${clip.id}`}>{clip.elo}</span>
          </div>
          <div className="flex items-center gap-1 font-mono-ui" title="Battle wins">
            <Flame className="h-3.5 w-3.5 text-[#FF2E55]" />
            {clip.wins}W / {clip.losses}L
          </div>
        </div>
      </div>
    </motion.article>
  );
}
